import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAITip } from "@/hooks/useAITip";
import { FREE_PLAN_CONDITION_IDS, TIP_LINKS } from "@/lib/tipConditions";
import { useIsPro } from "@/hooks/useIsPro";
import { useAuth } from "@/hooks/useAuth";

export function AITipCard() {
  const { businessId } = useAuth();
  const { tip, markShown, dismiss, isLoading } = useAITip();
  const isPro = useIsPro();

  useEffect(() => {
    if (tip?.id && !tip.shown_at) {
      markShown(tip.id);
    }
  }, [tip?.id, tip?.shown_at, markShown]);

  if (isLoading) return null;

  if (!tip) {
    if (businessId) {
      return (
        <p className="text-sm text-muted-foreground py-2">
          No new tip today. Tips are generated daily when your data matches an insight.
        </p>
      );
    }
    return null;
  }

  if (!isPro && !FREE_PLAN_CONDITION_IDS.has(tip.condition_id)) return null;

  const link = TIP_LINKS[tip.condition_id];
  const category = link?.category ?? "Insight";

  return (
    <Card
      data-testid="ai-tip-card"
      className="border-amber-500/30 bg-amber-500/5 shadow-sm overflow-hidden"
    >
      <CardContent className="p-4 relative">
        <button
          type="button"
          aria-label="Dismiss tip"
          className="absolute top-3 right-3 text-muted-foreground hover:text-foreground rounded p-1"
          onClick={() => dismiss(tip.id)}
        >
          ×
        </button>
        <div className="flex gap-3 pr-8">
          <span className="text-2xl shrink-0" aria-hidden>
            💡
          </span>
          <div className="space-y-2 min-w-0">
            <p className="text-sm text-foreground leading-snug">{tip.tip_text}</p>
            {link && (
              <div className="flex items-center gap-2 flex-wrap">
                <Button variant="secondary" size="sm" asChild>
                  <Link to={link.path}>{link.label}</Link>
                </Button>
                <span className="text-xs text-muted-foreground">{category}</span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
